import React, { forwardRef } from 'react';
import styles from './VoucherList/VoucherList.module.scss';
import VouchersCard from '~/shared/components/VouchersCard/VouchersCard';
import { CTA } from '~/shared/components/ProductCard/components/CTA';
import Typography from '~/shared/components/Typography/Typography';
import CloseIcon from '~/icons/close.svg';
import { useIsMobile } from '~/shared/hooks/useIsMobile/useIsMobile';
import { useTranslation } from '~/shared/utils/translation';

type Props = {
    description: string;
    a11yData: { voucherCount: number; index: number };
    barcode: string;
    expirationDate: string;
    isVoucherActive: boolean;
    isPendingReserve: boolean;
    isPendingCancel: boolean;
    onRedeem: () => void;
    onCancel: () => void;
    isFirstExtra: boolean;
};

export const VoucherItem = forwardRef<HTMLDivElement, Props>(
    (
        {
            description,
            a11yData,
            barcode,
            expirationDate,
            isVoucherActive,
            isPendingReserve,
            isPendingCancel,
            onRedeem,
            onCancel,
            isFirstExtra,
        },
        ref,
    ) => {
        const isMobile = useIsMobile();
        const { translate } = useTranslation();

        return (
            <>
                <div
                    ref={isFirstExtra ? ref : null}
                    className={styles.voucherContentWrapper}
                    aria-label={`Du er på nummer ${a11yData.index + 1} voucher ud af ${a11yData.voucherCount}. Denne voucher er ${
                        isVoucherActive
                            ? translate('basket.redeemed')
                            : `ikke ${translate('basket.redeemed')}`
                    } og giver følgende rabat: ${description}`}
                >
                    <VouchersCard
                        id={barcode}
                        title="SPAR 25% på et valgfrit produkt"
                        description="Kan kun bruges én gang"
                        expiryDate={expirationDate}
                        disabledButton={isVoucherActive || isPendingReserve}
                        redeemLabel={
                            isPendingReserve
                                ? translate('basket.redeeming')
                                : isVoucherActive
                                  ? translate('basket.redeemed')
                                  : translate('basket.redeem')
                        }
                        onRedeem={onRedeem}
                        variant={isMobile ? 'normal' : 'lite'}
                    />

                    {!isMobile && (
                        <CTA
                            aria-label={`${translate('basket.redeem')} brugen af nummer ${a11yData.index + 1} voucher til ved at trykke på denne knap`}
                            className={styles.cta}
                            disabled={isVoucherActive || isPendingReserve}
                            text={
                                isPendingReserve
                                    ? translate('basket.redeeming')
                                    : isVoucherActive
                                      ? translate('basket.redeemed')
                                      : translate('basket.redeem')
                            }
                            onClick={onRedeem}
                        />
                    )}
                </div>

                {isVoucherActive && (
                    <div className={styles.releaseVoucherWrapper}>
                        <div className={styles.releaseInfo}>
                            <Typography variant="paragraph-S" weight="bold" as="h4">
                                {translate('basket.discountAdded')}
                            </Typography>
                            <Typography variant="paragraph-S" as="p">
                                {barcode}
                            </Typography>
                        </div>

                        <button
                            onClick={onCancel}
                            className={styles.releaseButton}
                            type="button"
                            disabled={isPendingCancel}
                            aria-label={`${translate('basket.cancel')} brugen af nummer ${a11yData.index + 1} voucher ved at trykke på denne knap`}
                        >
                            <Typography variant="paragraph-S" as="span">
                                {isPendingCancel
                                    ? translate('basket.cancelling')
                                    : translate('basket.cancel')}
                            </Typography>

                            {!isPendingCancel && <CloseIcon height={14} width={10} />}
                        </button>
                    </div>
                )}
            </>
        );
    },
);

VoucherItem.displayName = 'VoucherItem';
